// Imports
import { useEffect, useRef } from 'react';
import { NavigationType, useLocation, useNavigationType } from 'react-router-dom';
import { useAppSelector } from '../../shareables/foundation/front-end/redux/hooks';
import sessionScopedStorage from '../../utils/session-scoped-storage';


// How long after network goes idle to stop retrying scroll restoration
const scrollRestorationIdleDelay = 300;


// Type guard
const isScrollState = (object: unknown): object is { disableScrollToTop?: boolean } => {
	if (typeof object !== 'object' || !object) {
		return false;
	}
	
	const value = (object as Record<string, unknown>).disableScrollToTop;
	return typeof value === 'boolean' || typeof value === 'undefined';
};


// Function component
const ScrollManager: React.FC = () => {
	// Use React Router functionality
	const location = useLocation();
	const navigationType = useNavigationType();
	
	
	// Use Redux functionality
	const activeNetworkRequestCount = useAppSelector((state) => state.common.activeNetworkRequestCount);
	
	
	// Stores the active scroll restoration observer so the idle-detection effect can disconnect it
	const scrollRestorationObserverRef = useRef<ResizeObserver | undefined>(undefined);
	
	
	// Stores the previous path to handle navigation away from the create panel
	const prevPathnameRef = useRef<string | undefined>();
	
	
	// Save scroll position when navigating away
	useEffect(() => {
		const savePosition = () => {
			sessionScopedStorage.setItem(`scroll:${location.key}`, String(window.scrollY));
		};
		
		window.addEventListener('beforeunload', savePosition);
		
		return () => {
			// Cleanup
			window.removeEventListener('beforeunload', savePosition);
			
			
			// Get existing saved position
			const existing = sessionScopedStorage.getItem(`scroll:${location.key}`);
			
			
			// Save position when required
			//  -> Allows scroll restoration to work when under React Strict Mode
			if (window.scrollY > 0 || existing === null) {
				savePosition();
			}
		};
	}, [location.key]);
	
	
	// Scroll to top on push/replace navigation
	useEffect(() => {
		// Skip back/forward navigation and page reload
		if (navigationType === NavigationType.Pop) {
			return;
		}
		
		
		// Track previous pathname
		const prevPathname = prevPathnameRef.current;
		prevPathnameRef.current = location.pathname;
		
		
		// Determine if the navigation is opening or closing a panel
		const isNavigatingFromPanel =
			prevPathname === `${location.pathname}/create` || prevPathname === `${location.pathname}/edit`;
		
		const isNavigatingToPanel =
			prevPathname !== undefined &&
			(location.pathname === `${prevPathname}/create` || location.pathname === `${prevPathname}/edit`);
		
		
		// Scroll to top when required, skipping panel navigation
		if (
			(!isScrollState(location.state) || !location.state.disableScrollToTop) &&
			!isNavigatingFromPanel &&
			!isNavigatingToPanel
		) {
			window.scrollTo(0, 0);
		}
	}, [location.pathname, location.state, navigationType]);
	
	
	// Restore the saved scroll position once content has loaded
	useEffect(() => {
		// Skip push/replace navigation
		if (navigationType !== NavigationType.Pop) {
			return;
		}
		
		
		// Get saved position
		const target = parseInt(sessionScopedStorage.getItem(`scroll:${location.key}`) ?? '0');
		
		
		// Skip if no saved position
		if (target === 0) {
			return;
		}
		
		
		// Scroll to the saved position
		window.scrollTo(0, target);
		
		
		// Retry as dynamic content loads and increases the page height
		const observer = new ResizeObserver(() => {
			if (window.scrollY < target) {
				window.scrollTo(0, target);
			} else {
				observer.disconnect();
				scrollRestorationObserverRef.current = undefined;
			}
		});
		
		observer.observe(document.body);
		scrollRestorationObserverRef.current = observer;
		
		return () => {
			observer.disconnect();
			scrollRestorationObserverRef.current = undefined;
		};
	}, [navigationType, location.key]);
	
	
	// Stop retrying scroll restoration once network activity becomes idle
	useEffect(() => {
		// Skip if network is still active or scroll restoration observer is not running
		if (activeNetworkRequestCount !== 0 || !scrollRestorationObserverRef.current) {
			return;
		}
		
		
		// Schedule observer disconnect after the idle delay
		const idleTimeout = setTimeout(() => {
			scrollRestorationObserverRef.current?.disconnect();
			scrollRestorationObserverRef.current = undefined;
		}, scrollRestorationIdleDelay);
		
		return () => {
			clearTimeout(idleTimeout);
		};
	}, [activeNetworkRequestCount]);
	
	
	// Render nothing
	return null;
};

export default ScrollManager;
